import { rtLogService } from '@rammetec/core';
import { repositoryFactory, DataSourceStrategy, CacheStrategy, } from '@rammetec/data-access';
import { USER_DATA, ORGANIZATION_USER_ORG_DATA } from '../types/user.js';
import { USER_ORGANIZATION } from '../types/organization.js';
import { OrgScopedCrudService } from './OrgScopedCrudService.js';
// Function to generate random 8-digit password
const generateRandomPassword = () => {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
};
const parseOptionalDate = (value) => {
    if (!value)
        return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};
class OrganizationUserService {
    constructor() {
        this.crud = new OrgScopedCrudService({
            collection: 'users',
            entity: ORGANIZATION_USER_ORG_DATA,
            dataSource: DataSourceStrategy.Firestore,
            cache: CacheStrategy.InMemory,
            lifecycle: true,
            audited: true,
        });
    }
    /**
     * Aggregate audit scope for writes whose root is the org-user record
     * (profile links, etc. — request 0257 §3.3).
     */
    async beginUserAudit(orgId, userId, ctx) {
        const existing = await this.crud.getById(orgId, userId);
        const record = (existing ?? { id: userId });
        return this.crud.beginAggregateAudit(orgId, { id: userId, version: null, record }, ctx);
    }
    async commitUserAuditBatch(orgId, batch, audit) {
        await this.crud.commitAggregateBatch(orgId, batch, audit);
    }
    /**
     * Creates or updates a user in the organization.
     * Creates the link between user and organization.
     * Creates the organization user record.
     *
     * @param params - The user data and organization ID
     * @param isNew - Whether this is a new user
     * @param authOperations - Auth operations for creating new users (required when isNew is true)
     * @param onAddUserToOrg - Optional callback to create user/<userId>/orgs/<orgId> via System API
     *   when client cannot write that path (Firebase rules). When provided, direct Firestore write is skipped.
     */
    async saveOrganizationUser(params, isNew, authOperations, onAddUserToOrg) {
        const { userData, orgId } = params;
        if (isNew) {
            if (!authOperations) {
                throw new Error('authOperations is required when creating a new user');
            }
            return await this.createOrganizationUser({
                userData: {
                    ...userData,
                    inactivationDate: userData.inactivationDate,
                },
                orgId,
            }, authOperations, onAddUserToOrg);
        }
        else {
            return await this.updateOrganizationUser({
                userId: userData.id,
                userData: {
                    name: userData.name,
                    email: userData.email,
                    role: userData.role,
                    inactivationDate: userData.inactivationDate,
                    allowOwnProfile: userData.allowOwnProfile,
                },
                orgId,
            });
        }
    }
    /**
     * Creates a new user in the organization.
     * Always creates in Auth first to get consistent ID.
     * When onAddUserToOrg is provided, the user-side link (user/<userId>/orgs/<orgId>) is done via
     * that callback (e.g. System API) instead of direct Firestore write.
     */
    async createOrganizationUser(params, authOperations, onAddUserToOrg) {
        const { userData, orgId } = params;
        if (!userData.email) {
            throw new Error('Email é obrigatório para criar novo usuário.');
        }
        let actualUserId;
        let userExists = false;
        let password = '';
        const userRepo = await repositoryFactory.getRepository('user', USER_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
        // If userId is provided, try to get user by ID
        if (userData.id) {
            try {
                await userRepo.getById(userData.id);
                actualUserId = userData.id;
                userExists = true;
            }
            catch {
                // User doesn't exist with that ID, will create new
                userExists = false;
            }
        }
        // If user doesn't exist, create it in Auth first (to get consistent ID)
        if (!userExists) {
            // Generate random password
            password = generateRandomPassword();
            const authResult = await authOperations.createUser(userData.email, password);
            actualUserId = authResult.user.uid;
            // Create user Firestore doc only when not delegating to system-api.
            // When onAddUserToOrg is provided the caller uses system-api, which creates
            // both the Auth user AND the Firestore user/{uid} doc server-side via Admin SDK
            // (client-side rules block creating user docs on behalf of another user).
            if (!onAddUserToOrg) {
                await userRepo.create({
                    id: actualUserId,
                    email: userData.email,
                    name: userData.name,
                    inclusionDate: new Date(),
                });
            }
            rtLogService.info("Usuário criado no sistema", {
                userId: actualUserId,
                email: userData.email,
            });
        }
        else {
            actualUserId = userData.id;
            // Update name/email in user collection if changed
            const existingUser = await userRepo.getById(actualUserId);
            if (existingUser && (existingUser.name !== userData.name || existingUser.email !== userData.email)) {
                await userRepo.update(actualUserId, {
                    name: userData.name,
                    email: userData.email,
                });
            }
        }
        // Create user-side link (user/<userId>/orgs/<orgId>): via callback (System API) or direct Firestore
        if (onAddUserToOrg) {
            await onAddUserToOrg(orgId, actualUserId, userData.role || 'user', userData.allowOwnProfile ?? false, userData.aiAssistantEnabled);
        }
        else {
            const userOrgLinkRepo = await repositoryFactory.getRepository(`user/${actualUserId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            try {
                await userOrgLinkRepo.getById(orgId);
            }
            catch {
                const link = {
                    id: orgId,
                    orgId: orgId,
                };
                if (typeof userData.aiAssistantEnabled === 'boolean') {
                    link.aiAssistantEnabled = userData.aiAssistantEnabled;
                }
                await userOrgLinkRepo.create(link);
            }
        }
        const inactivationDate = parseOptionalDate(userData.inactivationDate);
        const orgPayload = {
            role: userData.role || 'user',
            inactivationDate,
            allowOwnProfile: userData.allowOwnProfile || false,
        };
        const existingOrgUser = await this.crud.getById(orgId, actualUserId);
        if (existingOrgUser) {
            await this.crud.update(orgId, actualUserId, orgPayload);
        }
        else {
            await this.crud.create(orgId, {
                id: actualUserId,
                ...orgPayload,
            });
        }
        rtLogService.info("Usuário adicionado à organização", {
            userId: actualUserId,
            orgId,
            email: userData.email,
            name: userData.name,
            role: userData.role || 'user',
        });
        return {
            userId: actualUserId,
            password: userExists ? undefined : password,
            userCreated: !userExists,
        };
    }
    /**
     * Updates an existing user in the organization.
     * Updates name/email in user collection and role/inactivationDate in organization/users.
     */
    async updateOrganizationUser(params) {
        const { userId, userData, orgId } = params;
        const userRepo = await repositoryFactory.getRepository('user', USER_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
        // Update name/email in user collection if provided
        if (userData.name !== undefined || userData.email !== undefined) {
            const updateUserData = {};
            if (userData.name !== undefined)
                updateUserData.name = userData.name;
            if (userData.email !== undefined)
                updateUserData.email = userData.email;
            await userRepo.update(userId, updateUserData);
        }
        const inactivationDate = parseOptionalDate(userData.inactivationDate);
        const updateOrgData = {};
        if (userData.role !== undefined)
            updateOrgData.role = userData.role;
        // Firestore requires null instead of undefined for optional fields
        updateOrgData.inactivationDate = inactivationDate;
        if (userData.allowOwnProfile !== undefined)
            updateOrgData.allowOwnProfile = userData.allowOwnProfile;
        await this.crud.update(orgId, userId, updateOrgData);
        rtLogService.info("Usuário da organização alterado", {
            userId,
            orgId,
            email: userData.email,
            name: userData.name,
            role: userData.role,
            changes: { user: userData.name || userData.email ? { name: userData.name, email: userData.email } : {}, org: updateOrgData },
        });
        const updatedUser = await userRepo.getById(userId);
        if (!updatedUser) {
            throw new Error(`User ${userId} not found`);
        }
        const updatedOrgUser = await this.crud.getById(orgId, userId);
        if (!updatedOrgUser) {
            throw new Error(`Organization user data for ${userId} not found`);
        }
        return { userData: updatedUser, orgUserData: updatedOrgUser };
    }
    /**
     * Removes a user from the organization.
     * Also removes the link between user and organization.
     */
    async removeOrganizationUser(userId, organizationId) {
        await this.crud.delete(organizationId, userId);
        // Remove link from user organizations
        const userOrgLinkRepo = await repositoryFactory.getRepository(`user/${userId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
        try {
            await userOrgLinkRepo.delete(organizationId);
        }
        catch {
            // Link might not exist, that's okay
            rtLogService.warn("Could not remove user-organization link", { userId, organizationId });
        }
        rtLogService.info("Usuário removido da organização", {
            userId,
            organizationId,
        });
    }
    /**
     * Returns all org-user records for the given organization.
     * Each record lives at `org/<orgId>/users/<userId>`.
     */
    async getOrganizationUsers(orgId) {
        return this.crud.list(orgId);
    }
    /**
     * Sets the current profile for a user in a specific organization.
     * Outside the field-audit funnel (request 0257 §5 / §6).
     */
    async setUserCurrentProfile(organizationId, userId, profileId) {
        try {
            const organizationUserRepo = await repositoryFactory.getRepository(`org/${organizationId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            // Get existing org user data or create if doesn't exist
            try {
                const orgUserData = await organizationUserRepo.getById(userId);
                // Update existing org user data
                await organizationUserRepo.update(userId, {
                    ...orgUserData,
                    currentProfile: profileId,
                });
            }
            catch {
                // Org user data doesn't exist, create it
                const newOrgUserData = {
                    id: userId,
                    currentProfile: profileId,
                };
                await organizationUserRepo.create(newOrgUserData);
            }
            rtLogService.info('Current profile set for user in organization', { userId, organizationId, profileId });
        }
        catch (error) {
            rtLogService.error('Error setting current profile', { userId, organizationId, profileId, error });
            throw error;
        }
    }
    /**
     * Clears the current profile for a user in a specific organization.
     * Outside the field-audit funnel (request 0257 §5 / §6).
     */
    async clearUserCurrentProfile(organizationId, userId) {
        try {
            const organizationUserRepo = await repositoryFactory.getRepository(`org/${organizationId}/users`, ORGANIZATION_USER_ORG_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            // Get existing org user data
            try {
                const orgUserData = await organizationUserRepo.getById(userId);
                // Only update if currentProfile exists
                if (orgUserData && orgUserData.currentProfile !== undefined) {
                    await organizationUserRepo.update(userId, {
                        ...orgUserData,
                        currentProfile: null,
                    });
                }
            }
            catch {
                // Org user data doesn't exist, nothing to clear
                return;
            }
            rtLogService.info('Current profile cleared for user in organization', { userId, organizationId });
        }
        catch (error) {
            rtLogService.error('Error clearing current profile', { userId, organizationId, error });
            throw error;
        }
    }
}
export const organizationUserService = new OrganizationUserService();
//# sourceMappingURL=organizationUserService.js.map